import React, { useState, useEffect, useCallback } from 'react'
import { CheckboxProps, InputProps } from '@chakra-ui/react'
import { useFormikContext, Formik } from 'formik'
import { withFormikDevtools } from 'formik-devtools-extension'
import isNil from 'lodash.isnil'
import { buildAccountDisplayText, buildToAccountDisplayText } from '../../utils/optionBuilders'
import { CashJournalForm } from './'
import { useAccountLookup, useCashJournalConstraints, useCashJournalSubmission, useIsThirdParty } from '../../api'
import { CashJournalModal } from '../CashJournalModal'
import { JournalVariation, CashJournalValues } from './Types'
import {
  ContributionConstraints,
  DistributionConstraints,
  RetirementConstraintsResponse,
  SearchAccountsResponsePayload,
  AccountDetailsState
} from '@Types'
import { MORE_THAN_TWO_DECIMAL_PLACES, delayFn } from '../../utils'
import { ACCOUNT_RESTRICTION_WARNING, ERROR_RETRIEVING_DATA } from '../../constants'
import numberInputFormatter from '../../utils/inputFormatters'
import { useFormatNumber } from '../../utils/intlHelpers'
import { amtValid } from '../../utils/transferFormValidation'
import { cashJournalSchema, isNextDisabled } from './'
import { useFormatMessage } from '../../utils/intlHelpers'
import { useCreateScheduleTransfer } from '../../api/useCreateScheduledTransfer'
import useGetSchedules from '../../api/useGetSchedules'
import formatPayload from './formatPayload'
import useGetRecentCashActivity from '../../api/useGetRecentCashActivity'
import { useQueryClient } from 'react-query'
import queryKeys from '../../api/queryKeys'

export interface CashJournalWrapperProps {
  account: SearchAccountsResponsePayload
  amountAvailable: number
  accountDetails?: AccountDetailsState | null
  hasSubmitPermission: boolean
  constraintData?: RetirementConstraintsResponse
  setAccountLookupNum: React.Dispatch<React.SetStateAction<string>>
  accountLookupNum: string
}

const CashJournalWrapper = ({
  account,
  amountAvailable,
  hasSubmitPermission,
  constraintData,
  setAccountLookupNum,
  accountLookupNum
}: CashJournalWrapperProps) => {
  const [isModalShowing, setIsModalShowing] = useState<boolean>(false)
  const [iraDistributionConstraints, setIraDistributionConstraints] = useState<DistributionConstraints | null>(null)
  const [iraContributionConstraints, setIraContributionConstraints] = useState<ContributionConstraints | null>(null)
  const formik = useFormikContext<CashJournalValues>()
  // TODO: This state looks abandoned: might need to delete.

  const [warningMessageDescKey, setWarningMessageDescKey] = useState<string>('')
  const [warningMessageTitleKey, setWarningMessageTitleKey] = useState<string>('')
  const [lookUpError, setLookUpError] = useState<string>('')
  const { data, error, isLoading, refetch: searchAccountLookup } = useAccountLookup(formik.values.toAccount!)
  const { refetch } = useGetRecentCashActivity(account.accountNumber)
  const { refetch: refetchSchedulesData } = useGetSchedules(account.accountNumber)
  const queryClient = useQueryClient()

  const { mutate: submitCashJournal, isLoading: isSubmittingTransfer } = useCashJournalSubmission({
    onSuccess: () => {
      resetEverythingMemo()
      refetchSchedulesData()
      formik.resetForm()
      delayFn(refetch)
    },
    onSettled: () => {
      setIsModalShowing(false)
    },
    amountAvailable
  })

  const {
    mutate: submitCashJournalScheduledTransfer,
    isLoading: isSubmittingScheduledTransfer
  } = useCreateScheduleTransfer('journal', '', {
    onSuccess: () => {
      resetEverythingMemo()
      refetchSchedulesData()
      formik.resetForm()
    },
    onSettled: () => {
      setIsModalShowing(false)
    }
  })

  const isScheduledTransfer =
    formik.values.transferRecurrence === 'RECURRING' || formik.values.whenToSend === 'IN_THE_FUTURE'

  const [formVariation, setFormVariation] = useState<JournalVariation>(JournalVariation.incomplete)
  const { isThirdPartyData, isThirdPartyError } = useIsThirdParty(formik.values.fromAccount!, accountLookupNum)
  const formatMessage = useFormatMessage()

  const resetEverything = () => {
    setIsModalShowing(false)
    setAccountLookupNum('')
    setWarningMessageDescKey('')
    setFormVariation(JournalVariation.incomplete)
    formik.resetForm()
  }

  const resetEverythingMemo = useCallback(resetEverything, [])
  const numberFormatter = useFormatNumber()

  useEffect(() => {
    if (constraintData?.distributionConstraints) {
      setIraDistributionConstraints(constraintData.distributionConstraints)
    }
    if (constraintData?.contributionConstraints) {
      setIraContributionConstraints(constraintData.contributionConstraints)
    }
    if (constraintData) {
      setFormVariation(handleVariation(constraintData))
    }
  }, [constraintData])

  const formatMessageMemo = useCallback(formatMessage, [])

  useEffect(() => {
    if (!!error) {
      setLookUpError('Account not found. Try again.')
    } else {
      setLookUpError('')
    }
    if (data?.restrictionCode) {
      setWarningMessageDescKey(ACCOUNT_RESTRICTION_WARNING)
    } else if (Number(error?.response?.status) >= 500 || isThirdPartyError) {
      setWarningMessageDescKey(ERROR_RETRIEVING_DATA)
    } else if (isThirdPartyData?.isThirdParty && isThirdPartyData?.reason) {
      setWarningMessageDescKey(
        `${formatMessageMemo('transfers.cashJournal.thirdParty.details')} ${isThirdPartyData?.reason}`
      )
      setWarningMessageTitleKey(formatMessageMemo('transfers.cashJournal.thirdParty.title'))
    } else {
      setWarningMessageDescKey('')
      setWarningMessageTitleKey('')
    }
  }, [data, error, isThirdPartyData, isThirdPartyError, formatMessageMemo])

  useEffect(() => {
    setWarningMessageDescKey('')
  }, [formik.values.toAccount])

  const handleVariation = ({ distributionConstraints, contributionConstraints }: RetirementConstraintsResponse) => {
    if (!isNil(distributionConstraints) && !isNil(contributionConstraints)) {
      return JournalVariation.iraToIra
    }
    if (!isNil(distributionConstraints) && isNil(contributionConstraints)) {
      return JournalVariation.iraToInd
    }
    if (isNil(distributionConstraints) && !isNil(contributionConstraints)) {
      return JournalVariation.indToIra
    }
    if (isNil(distributionConstraints) && isNil(contributionConstraints)) {
      return JournalVariation.indToInd
    }
    return JournalVariation.incomplete
  }

  const handleBlur = (field: string) => {
    formik.setFieldTouched(field)
  }
  const setFieldValue = (field: string, value: any) => {
    formik.setFieldValue(field, value)
  }
  const handleChange = (e: React.FormEvent<HTMLInputElement>, data: InputProps | CheckboxProps) => {
    if (!isNil(data?.checked)) {
      formik.setFieldValue('totalBalanceCheckbox', data.checked)
      if (data?.checked) {
        formik.setFieldValue('transferAmountAsCurrency', '')
        formik.setFieldValue('transferAmount', 0)
      }
    }

    // If the user has entered more than 2 decimal points ignore input
    if ((data?.value as string)?.search(MORE_THAN_TWO_DECIMAL_PLACES) !== -1) {
      return
    }
    const fieldData = numberInputFormatter(data.value as string, numberFormatter)
    fieldData.error = !amtValid(fieldData.raw)
    formik.setFieldValue('transferAmountAsCurrency', fieldData.display)
    formik.setFieldValue('transferAmount', fieldData.raw || 0)
  }

  const handleLookupChange = (e: React.ChangeEvent, data: string) => {
    formik.setFieldValue('toAccount', data)
    queryClient.invalidateQueries(queryKeys.accountLookup)
  }
  const handleLookupSubmit = (account: string) => {
    formik.setValues({
      ...formik.values,
      toAccount: account,
      contributionType: '',
      contributionReason: '',
      distributionReason: '',
      federalTaxWithholdingAmountDisplay: '',
      federalTaxWithholdingAmountRaw: undefined,
      federalTaxWithholdingType: undefined,
      stateTaxWithholdingAmountDisplay: '',
      stateTaxWithholdingAmountRaw: undefined,
      stateTaxWithholdingType: undefined
    })
    searchAccountLookup()
    setAccountLookupNum(account)
  }
  const handleSaveData = (fieldName: string, value: string) => {
    formik.setFieldValue(fieldName, value)
  }
  const handleChangeIRA = (key: string, value: any, section?: string) => {
    if (section === 'iraContribution' && key === 'contributionType' && !isNil(value)) {
      formik.setFieldValue('contributionType', value)
    }
    if (section === 'iraContribution' && key === 'contributionReason' && !isNil(value)) {
      formik.setFieldValue('contributionReason', value)
    }
    if (section === 'iraDistribution' && key === 'distributionReason' && !isNil(value)) {
      formik.setFieldValue('distributionReason', value)
    }
    if (key === 'federal') {
      formik.setValues({
        ...formik.values,
        federalTaxWithholdingType: value.type.value,
        federalTaxWithholdingAmountRaw: value.amount.raw
      })
    } else if (key === 'state') {
      formik.setValues({
        ...formik.values,
        stateTaxWithholdingType: value.type.value,
        stateTaxWithholdingAmountRaw: value.amount.raw
      })
    }
    if (key === 'applyToPreviousYear') {
      formik.setFieldValue('applyToPriorYear', value)
    }
    if ((key === 'stateWithholdingType' || key === 'federalWithholdingType') && isNil(value)) {
      formik.setValues({
        ...formik.values,
        federalTaxWithholdingType: undefined,
        federalTaxWithholdingAmountRaw: undefined,
        stateTaxWithholdingType: undefined,
        stateTaxWithholdingAmountRaw: undefined
      })
    }
  }

  const handleSubmit = () => {
    if (isScheduledTransfer) {
      submitCashJournalScheduledTransfer(formatPayload(formik.values, isScheduledTransfer))
    } else {
      submitCashJournal(formatPayload(formik.values, isScheduledTransfer))
    }
  }

  return (
    <>
      <CashJournalForm
        values={formik.values}
        accountFromDisplay={buildAccountDisplayText(amountAvailable, account?.accountNumber, account?.accountTitle)}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        handleChange={handleChange}
        handleLookupChange={handleLookupChange}
        onSubmit={formik.handleSubmit}
        touched={formik.touched}
        iraDistributionConstraints={iraDistributionConstraints}
        iraContributionConstraints={iraContributionConstraints}
        iraConstraints={constraintData}
        formVariation={constraintData ? formVariation : JournalVariation.incomplete}
        loading={isLoading}
        warningMessage={warningMessageDescKey}
        warningMessageTitle={warningMessageTitleKey}
        accountLookupData={{ ...data, accountNumber: accountLookupNum }}
        onLookupSubmit={(acc: string) => handleLookupSubmit(acc)}
        accountLookUpError={lookUpError}
        handleSaveData={handleSaveData}
        handleChangeIRA={handleChangeIRA}
        isNextDisabled={isNextDisabled(formVariation, formik) || !formik.isValid}
        setIsModalShowing={setIsModalShowing}
      />
      <CashJournalModal
        isModalShowing={isModalShowing}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmittingTransfer || isSubmittingScheduledTransfer}
        handleShowingModal={setIsModalShowing}
        formikValues={formik.values}
        fromAccountDisplayText={
          amountAvailable ? buildAccountDisplayText(amountAvailable, account?.accountNumber, account?.accountTitle) : ''
        }
        toAccountDisplayText={buildToAccountDisplayText(
          formik.values.toAccount!,
          data?.accountTitle!,
          'No account provided'
        )}
        formVariation={formVariation}
        hasSubmitPermission={hasSubmitPermission}
        isThirdParty={isThirdPartyData?.isThirdParty}
        isScheduledTransfer={isScheduledTransfer}
        amountAvailable={amountAvailable}
      />
    </>
  )
}

export interface CashJournalFormikWrapperProps {
  account: SearchAccountsResponsePayload
  amountAvailable: number
  accountDetails?: AccountDetailsState | null
  hasSubmitPermission: boolean
}

export const CashJournalFormikWrapper = ({
  account,
  amountAvailable,
  accountDetails,
  hasSubmitPermission
}: CashJournalFormikWrapperProps) => {
  const [accountLookupNum, setAccountLookupNum] = useState<string>('')
  const { constraintData } = useCashJournalConstraints(account.accountNumber, accountLookupNum)

  const formikDefaultValues = {
    initialValues: cashJournalSchema(
      constraintData?.distributionConstraints,
      constraintData?.contributionConstraints,
      amountAvailable
    ).cast({ fromAccount: account.accountNumber }),
    onSubmit: (values: any) => {},
    validationSchema: () =>
      cashJournalSchema(
        constraintData?.distributionConstraints,
        constraintData?.contributionConstraints,
        amountAvailable
      )
  }
  return (
    <Formik {...formikDefaultValues}>
      {(formikProps) => {
        withFormikDevtools(formikProps)
        return (
          <CashJournalWrapper
            account={account}
            amountAvailable={amountAvailable}
            accountDetails={accountDetails}
            hasSubmitPermission={hasSubmitPermission}
            constraintData={constraintData}
            setAccountLookupNum={setAccountLookupNum}
            accountLookupNum={accountLookupNum}
          />
        )
      }}
    </Formik>
  )
}
